/**
 * imageGuards.js
 * Keeps base64 imagery in the report payload within a size the API will actually accept.
 *
 * The report POST carries the leaf photo, the soil photo, up to 14 satellite band images and
 * one map tile per device. Unbounded, that payload runs to tens of megabytes and is rejected
 * by nginx (413) long before FastAPI sees it. These helpers downscale first and, only if the
 * total is still over budget, drop the least-important images in a defined order.
 */

export const MAX_IMAGE_EDGE_PX = 1280;
export const MAX_IMAGE_BYTES = 700000;      // decoded bytes, per photo
export const MAX_SAT_IMAGE_EDGE_PX = 900;
export const MAX_SAT_IMAGE_BYTES = 400000;  // decoded bytes, per satellite band
export const MAX_MAP_IMAGE_EDGE_PX = 1024;
export const MAX_MAP_IMAGE_BYTES = 500000;  // decoded bytes, per device map tile
export const MAX_TOTAL_IMAGE_BYTES = 12000000; // decoded bytes, whole payload

/** Decoded byte size of a data URI. Returns 0 for anything that is not one. */
export function dataUrlBytes(dataUrl) {
  if (typeof dataUrl !== 'string') return 0;
  const comma = dataUrl.indexOf(',');
  if (comma === -1) return 0;
  const b64 = dataUrl.slice(comma + 1);
  if (!b64) return 0;
  let padding = 0;
  if (b64.endsWith('==')) padding = 2;
  else if (b64.endsWith('=')) padding = 1;
  return Math.max(0, Math.floor((b64.length * 3) / 4) - padding);
}

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = dataUrl;
  });
}

/**
 * Re-encode a data URI so it fits within maxEdge and maxBytes.
 * Returns the new data URI, or null if it could not be brought under budget.
 *
 * Already-conforming images are returned untouched so small PNGs are not needlessly
 * degraded by a lossy round trip.
 */
export async function capDataUrl(dataUrl, options = {}) {
  const {
    maxEdge = MAX_IMAGE_EDGE_PX,
    maxBytes = MAX_IMAGE_BYTES,
    mime = 'image/jpeg',
  } = options;

  if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image')) return null;

  try {
    const img = await loadImage(dataUrl);
    const longest = Math.max(img.width, img.height);
    if (!longest) return null;

    // Already small enough on both axes: leave it alone.
    if (longest <= maxEdge && dataUrlBytes(dataUrl) <= maxBytes) return dataUrl;

    let scale = Math.min(1, maxEdge / longest);
    const qualities = [0.72, 0.6, 0.5, 0.42];

    for (let attempt = 0; attempt < 5; attempt += 1) {
      const quality = qualities[Math.min(attempt, qualities.length - 1)];
      // After the quality ladder is exhausted, start shrinking instead.
      if (attempt >= qualities.length) scale *= 0.75;

      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      // JPEG has no alpha; paint white so transparent PNGs do not come out black.
      if (mime === 'image/jpeg') {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, w, h);
      }
      ctx.drawImage(img, 0, 0, w, h);

      const out = canvas.toDataURL(mime, quality);
      if (dataUrlBytes(out) <= maxBytes) return out;
    }
    return null;
  } catch (e) {
    return null;
  }
}

/** Sum the decoded bytes of every data URI reachable in the payload. */
function totalImageBytes(payload) {
  let total = 0;
  const sat = payload.satellite_data || {};
  Object.keys(sat).forEach(k => { total += dataUrlBytes(sat[k] && sat[k].map); });
  (payload.device_imagery || []).forEach(d => { total += dataUrlBytes(d && d.image_base64); });
  ((payload.advanced_ai && payload.advanced_ai.attachments) || [])
    .forEach(a => { total += dataUrlBytes(typeof a === 'string' ? a : a && a.image_base64); });
  total += dataUrlBytes(payload.leaf_input_image_base64);
  total += dataUrlBytes(payload.soil_input_image_base64);
  return total;
}

/**
 * Enforce MAX_TOTAL_IMAGE_BYTES by dropping images in the contract's priority order.
 * Never drops the leaf photo, the soil photo, the current RGB satellite tile, or the
 * first device map — those carry the report's primary evidence.
 *
 * @returns {{payload: object, dropped: string[]}}
 */
export function enforceImageBudget(payload) {
  const dropped = [];
  if (!payload || typeof payload !== 'object') return { payload, dropped };

  // Shallow-clone the containers we mutate; leave the caller's object intact.
  const next = { ...payload };
  next.satellite_data = { ...(payload.satellite_data || {}) };
  next.device_imagery = Array.isArray(payload.device_imagery) ? [...payload.device_imagery] : [];
  if (payload.advanced_ai) {
    next.advanced_ai = { ...payload.advanced_ai };
    if (Array.isArray(payload.advanced_ai.attachments)) {
      next.advanced_ai.attachments = [...payload.advanced_ai.attachments];
    }
  }

  const satKeys = Object.keys(next.satellite_data);
  const isRgb = k => k.endsWith('_rgb') || k === 'rgb';

  // Build the drop queue, least valuable first.
  const queue = [];
  satKeys.filter(k => k.startsWith('old_') && !isRgb(k))
    .forEach(k => queue.push({ kind: 'sat', key: k }));
  for (let i = 1; i < next.device_imagery.length; i += 1) queue.push({ kind: 'device', index: i });
  const atts = (next.advanced_ai && next.advanced_ai.attachments) || [];
  for (let i = 0; i < atts.length; i += 1) queue.push({ kind: 'attachment', index: i });
  satKeys.filter(k => k.startsWith('old_') && isRgb(k))
    .forEach(k => queue.push({ kind: 'sat', key: k }));
  satKeys.filter(k => k.startsWith('new_') && !isRgb(k))
    .forEach(k => queue.push({ kind: 'sat', key: k }));

  for (const item of queue) {
    if (totalImageBytes(next) <= MAX_TOTAL_IMAGE_BYTES) break;

    if (item.kind === 'sat') {
      const entry = next.satellite_data[item.key];
      if (entry && entry.map) {
        next.satellite_data[item.key] = { ...entry, map: null, unavailable: true };
        dropped.push(`satellite:${item.key}`);
      }
    } else if (item.kind === 'device') {
      const d = next.device_imagery[item.index];
      if (d && d.image_base64) {
        next.device_imagery[item.index] = { ...d, image_base64: null, unavailable: true };
        dropped.push(`device_imagery:${item.index}`);
      }
    } else if (item.kind === 'attachment') {
      const list = next.advanced_ai.attachments;
      if (list[item.index]) {
        list[item.index] = null;
        dropped.push(`advanced_ai.attachment:${item.index}`);
      }
    }
  }

  if (next.advanced_ai && Array.isArray(next.advanced_ai.attachments)) {
    next.advanced_ai.attachments = next.advanced_ai.attachments.filter(Boolean);
  }

  return { payload: next, dropped };
}
